import clsx from "clsx";

import { Icon, IconName } from "../icons";

import styles from "./ActivityRail.module.css";

export interface ActivityRailItem<Id extends string = string> {
  id: Id;
  label: string;
  icon: IconName;
  disabled?: boolean;
  title?: string;
}

export interface ActivityRailProps<Id extends string = string> {
  items: ActivityRailItem<Id>[];
  active: Id | null;
  onSelect: (id: Id) => void;
  className?: string;
}

/**
 * Vertical activity bar (VS Code style) hosting sidebar entries. Always
 * visible; the active item's panel opens to its left.
 */
export const ActivityRail = <Id extends string = string>({
  items,
  active,
  onSelect,
  className,
}: ActivityRailProps<Id>) => (
  <div
    className={clsx(styles.rail, className)}
    role="tablist"
    aria-orientation="vertical"
  >
    {items.map((item) => {
      const isActive = active === item.id;
      return (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          disabled={item.disabled}
          title={item.title ?? item.label}
          className={clsx(styles.item, isActive && styles.itemActive)}
          onClick={() => onSelect(item.id)}
        >
          <Icon name={item.icon} className={styles.icon} />
          <span className={clsx(styles.label, "text-size-smallest")}>
            {item.label}
          </span>
        </button>
      );
    })}
  </div>
);
